import {
  useCreateLease,
  useDeleteLease,
  useListLeases,
  useListResidents,
  useUpdateLease,
  type LeaseOut,
  type LeaseTermsIn,
} from '@myrentalmate/api-client';
import { Chip, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router';

import {
  optional,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { StatRow, StatTile } from '../../components/Stats';
import { listResult, okItems } from '../../lib/apiData';
import { formatDate, formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { useDashboardSummary } from '../../lib/summary';
import { useUnitLabels } from '../properties/useUnitLabels';

const TERM_FIELDS: Field[] = [
  { name: 'start_date', label: 'Start Date', type: 'date', required: true },
  {
    name: 'end_date',
    label: 'End Date',
    type: 'date',
    helperText: 'Leave blank for month to month',
  },
  { name: 'rent_amount', label: 'Monthly Rent', type: 'number', required: true },
  {
    name: 'due_day',
    label: 'Rent Due Day',
    type: 'number',
    required: true,
    defaultValue: '1',
    helperText: 'Day of the month, 1 to 28',
  },
  { name: 'deposit_amount', label: 'Security Deposit', type: 'number' },
  { name: 'notes', label: 'Notes', type: 'multiline' },
];

const COLUMNS: Column<LeaseOut>[] = [
  {
    label: 'Rental',
    render: (l) => (
      <MuiLink component={Link} to={`/staff/leases/${l.id}`}>
        {l.property_name} · {l.unit_number}
      </MuiLink>
    ),
  },
  { label: 'Residents', render: (l) => l.residents.map((r) => r.name).join(', ') || '–' },
  { label: 'Rent', render: (l) => formatMoney(l.rent_amount) },
  { label: 'Ends', render: (l) => (l.end_date ? formatDate(l.end_date) : 'Month to month') },
  {
    label: 'Balance',
    render: (l) => {
      const balance = Number(l.balance);
      if (balance > 0) return <Chip size="small" color="error" label={formatMoney(l.balance)} />;
      return balance < 0 ? `${formatMoney(-balance)} credit` : formatMoney(0);
    },
  },
  {
    label: 'Status',
    render: (l) => (
      <Chip
        size="small"
        label={l.status === 'active' ? 'Active' : 'Ended'}
        color={l.status === 'active' ? 'success' : 'default'}
      />
    ),
  },
];

function termsBody(values: FormValues): LeaseTermsIn {
  return {
    start_date: required(values, 'start_date'),
    end_date: optional(values, 'end_date'),
    rent_amount: required(values, 'rent_amount'),
    due_day: Number(required(values, 'due_day')),
    deposit_amount: optional(values, 'deposit_amount') ?? '0',
    notes: optional(values, 'notes'),
  };
}

function toValues(l: LeaseOut): FormValues {
  return {
    start_date: l.start_date,
    end_date: l.end_date ?? '',
    rent_amount: Number(l.rent_amount).toFixed(2),
    due_day: String(l.due_day),
    deposit_amount: Number(l.deposit_amount).toFixed(2),
    notes: l.notes ?? '',
  };
}

function useRows({ q, page, pageSize }: ListParams) {
  return listResult(useListLeases({ q, page, page_size: pageSize }));
}

export function LeasesPage() {
  const canManage = useCan('leases.manage');
  const create = useCreateLease();
  const update = useUpdateLease();
  const remove = useDeleteLease();
  const summary = useDashboardSummary();
  const unitLabels = useUnitLabels();
  const residents = okItems(useListResidents({ page_size: 200 }).data);

  const fields: Field[] = [
    {
      name: 'unit_id',
      label: 'Unit',
      type: 'select',
      required: true,
      options: [...unitLabels].map(([id, label]) => ({ value: String(id), label })),
    },
    {
      name: 'resident_id',
      label: 'Primary Resident',
      type: 'select',
      required: true,
      helperText: 'Add co-residents from the lease page',
      options: residents.map((r) => ({
        value: String(r.id),
        label: `${r.last_name}, ${r.first_name}`,
      })),
    },
    ...TERM_FIELDS,
  ];

  return (
    <RecordPage
      title="Leases"
      singular="lease"
      description="Who rents what, on what terms, and what they owe. Open a lease to take a payment."
      summary={
        <StatRow>
          <StatTile label="Active Leases" value={summary?.active_leases} />
          <StatTile label="Owed by Residents" value={summary && formatMoney(summary.amount_owed)} />
          <StatTile label="Leases Owing" value={summary?.leases_owing} />
          <StatTile label="Ending Within 60 Days" value={summary?.leases_ending_soon} />
        </StatRow>
      }
      fields={fields}
      editFields={TERM_FIELDS}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={toValues}
      create={(values) =>
        create.mutateAsync({
          data: {
            ...termsBody(values),
            unit_id: Number(required(values, 'unit_id')),
            resident_ids: [Number(required(values, 'resident_id'))],
          },
        })
      }
      update={(leaseId, values) => update.mutateAsync({ leaseId, data: termsBody(values) })}
      remove={(leaseId) => remove.mutateAsync({ leaseId })}
    />
  );
}
